'use client';

import { ReactNode, useEffect, useMemo } from 'react';

import { configureCache } from '@growthbook/growthbook';
import { GrowthBook, GrowthBookPayload, GrowthBookProvider } from '@growthbook/growthbook-react';

import { commonUiStateSlice, useAppSelector } from '@app/app-state';
import { environment } from '@app/environments/environment';

configureCache({ disableCache: true });

export const GrowthbookAppProvider = ({
    children,
    payload,
}: {
    children: ReactNode;
    payload?: GrowthBookPayload | null;
}) => {
    const reduxPayload = useAppSelector(commonUiStateSlice.selectors.selectGrowthbookPayload);
    const ftOverrides = useAppSelector(commonUiStateSlice.selectors.selectFt);

    const resolvedPayload = payload ?? reduxPayload;

    const gb = useMemo(() => {
        const instance = new GrowthBook({
            apiHost: environment.GROWTHBOOK.API_HOST,
            clientKey: environment.GROWTHBOOK.CLIENT_KEY,
        });

        if (resolvedPayload) {
            instance.initSync({ payload: resolvedPayload });
        }

        const overrides = Object.entries(ftOverrides).filter(([, v]) => v != null);
        if (overrides.length > 0) {
            instance.setForcedFeatures(new Map(overrides));
        }

        return instance;
    }, [resolvedPayload, ftOverrides]);

    useEffect(() => {
        return () => gb.destroy();
    }, [gb]);

    return <GrowthBookProvider growthbook={gb}>{children}</GrowthBookProvider>;
};
